// Lê uma planilha de inadimplência e devolve os códigos de UC que estão nela,
// para a aba de Rota de Cobrança já abrir com essas unidades marcadas.
//
// Existe porque a lista que vai a campo já vem refinada: o gestor exporta a
// inadimplência por cidade, decide no Excel quem vai visitar e, hoje, teria que
// reencontrar cada UC na tela para marcar de novo — conferindo número por número,
// que é justamente o trabalho manual que essa aba veio eliminar.
//
// A planilha esperada é o próprio export do sistema (`exportInadimplencia.ts`),
// que tem três abas — 'Resumo por Cliente', 'Faturas Detalhadas' e 'Metadados'.
// A coluna com os códigos é a 'UC' de 'Faturas Detalhadas'; em 'Resumo por
// Cliente' a coluna 'UCs' é a QUANTIDADE de unidades do cliente, não os números,
// e confundir as duas selecionaria a UC de número "4".

import { normalizeUcCodigo } from './ucCodigo.ts';

/** Rótulos aceitos como "esta coluna tem o código da UC".
 *  'ucs' está fora de propósito: no nosso export ela é a contagem. */
const ROTULOS_UC = new Set([
    'uc', 'uc codigo', 'codigo uc', 'numero uc', 'n uc', 'no uc',
    'unidade consumidora', 'unidades consumidoras',
]);

/** Preposicoes que so enfeitam o cabecalho: 'Nº da UC' e 'Nº UC' sao a mesma coisa. */
const CONECTIVOS = new Set(['da', 'de', 'do', 'das', 'dos']);

/** Sem acento, sem pontuação, sem conectivo, minúsculo — 'Nº da UC' vira 'n uc'. */
const normalizarRotulo = (v: unknown): string =>
    String(v ?? '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(p => p && !CONECTIVOS.has(p))
        .join(' ');

export interface UcsDaPlanilha {
    /** Códigos normalizados, sem repetição, na ordem em que apareceram. */
    codigos: string[];
    /** Nome da aba de onde vieram — vai na mensagem para o usuário conferir. */
    aba: string | null;
    /** Linhas com conteúdo na coluna que não eram código de UC (ex.: 'TOTAL'). */
    ignoradas: number;
}

/**
 * Extrai os códigos de UC de UMA aba já convertida em matriz de células.
 *
 * A busca do cabeçalho varre as primeiras linhas em vez de assumir a linha 1:
 * planilha exportada e depois editada à mão costuma ganhar título ou linha em
 * branco no topo, e travar na primeira linha faria a importação falhar por um
 * motivo que ninguém consegue adivinhar olhando a tela.
 */
export const extrairUcsDaMatriz = (linhas: unknown[][]): Omit<UcsDaPlanilha, 'aba'> | null => {
    const limite = Math.min(linhas.length, 15);
    for (let i = 0; i < limite; i++) {
        const linha = linhas[i] || [];
        const col = linha.findIndex(c => ROTULOS_UC.has(normalizarRotulo(c)));
        if (col === -1) continue;

        const codigos: string[] = [];
        const vistos = new Set<string>();
        let ignoradas = 0;

        for (let j = i + 1; j < linhas.length; j++) {
            const bruto = (linhas[j] || [])[col];
            if (bruto == null || String(bruto).trim() === '') continue;

            const codigo = normalizeUcCodigo(String(bruto));
            // Linha de TOTAL e qualquer texto sobram como 0 dígitos depois da
            // normalização; contá-las serve para avisar, não para falhar.
            if (!codigo || !/^\d+$/.test(codigo)) { ignoradas++; continue; }
            if (vistos.has(codigo)) continue;
            vistos.add(codigo);
            codigos.push(codigo);
        }
        return { codigos, ignoradas };
    }
    return null;
};

/**
 * Lê o arquivo inteiro e devolve as UCs da primeira aba que tiver coluna de UC.
 *
 * `xlsx` é importado sob demanda: são ~400 KB que só interessam a quem clica em
 * importar, e a Central de Inadimplência já é pesada o bastante para não carregar
 * isso na abertura.
 */
export const lerUcsDaPlanilha = async (arquivo: File): Promise<UcsDaPlanilha> => {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await arquivo.arrayBuffer(), { type: 'array' });

    for (const nome of wb.SheetNames) {
        const aba = wb.Sheets[nome];
        if (!aba) continue;
        const linhas = XLSX.utils.sheet_to_json<unknown[]>(aba, { header: 1, blankrows: false, raw: false });
        const achado = extrairUcsDaMatriz(linhas);
        if (achado && achado.codigos.length > 0) return { ...achado, aba: nome };
    }

    throw new Error(
        'Não encontrei uma coluna "UC" na planilha. Use o Excel exportado pela própria '
        + 'Central de Inadimplência, ou uma planilha com uma coluna chamada UC.');
};
